import { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { useQuery } from '@tanstack/react-query'
import api from '@/lib/api'
import { formatPercent, formatNumber } from '@/lib/utils'
import CytoscapeComponent from 'react-cytoscapejs'
import {
  Search,
  CheckCircle,
  Database,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  X,
  Network,
  Table2,
  FileText,
  Loader2,
  SlidersHorizontal,
} from 'lucide-react'

// ─── Types ───────────────────────────────────────────────────────────────────

interface BrowseFund {
  fund_id: number
  fund_name: string
  manager_name: string | null
  has_peer_data: boolean
}

interface BrowseResponse {
  funds: BrowseFund[]
  total: number
  page: number
  page_size: number
  total_pages: number
  cached_peer_count: number
  match_with_peers: number
  match_without_peers: number
}

interface GraphNode {
  id: string
  label: string
  depth: number
  strategy: string
  buckets: string[]
  has_peer_data: boolean
  role: 'ROOT' | 'FUND' | 'PEER_ONLY'
  ann_return?: number
  sharpe_ratio?: number
  max_drawdown?: number
  volatility?: number
  sortino_ratio?: number
  calmar_ratio?: number
  skewness?: number
  kurtosis?: number
  cumulative_return?: number
  best_month?: number
  worst_month?: number
}

interface GraphEdge {
  source: string
  target: string
  order: number
  mutual: boolean
  bucket: string
}

interface GraphResponse {
  nodes: GraphNode[]
  edges: GraphEdge[]
  all_buckets: string[]
  stats: {
    node_count: number
    edge_count: number
    root_fund_id: number
    max_depth: number
  }
}

interface PeerBucket {
  fundBucketName: string
  strategy?: string
  type?: string
  isDefaultFundBucket?: boolean
  peerFunds?: { fundId: number; fundName: string; managerName?: string }[]
  peerIndices?: { indexId: number; indexName: string }[]
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type CyStylesheet = any

// ─── Layout helpers ──────────────────────────────────────────────────────────

function computeRadialPositions(
  nodes: GraphNode[],
  _rootId: string,
  width: number,
  height: number,
): Record<string, { x: number; y: number }> {
  const cx = width / 2
  const cy = height / 2
  const ringSpacing = Math.min(width, height) / 4

  const depthGroups: Record<number, GraphNode[]> = {}
  nodes.forEach((n) => {
    const d = n.depth ?? 0
    if (!depthGroups[d]) depthGroups[d] = []
    depthGroups[d].push(n)
  })

  const positions: Record<string, { x: number; y: number }> = {}
  Object.entries(depthGroups).forEach(([dStr, group]) => {
    const d = Number(dStr)
    if (d === 0) {
      group.forEach((n) => {
        positions[n.id] = { x: cx, y: cy }
      })
      return
    }
    const radius = d * ringSpacing
    const n = group.length
    group.forEach((node, i) => {
      const angle = (2 * Math.PI * i) / n - Math.PI / 2
      positions[node.id] = {
        x: cx + radius * Math.cos(angle),
        y: cy + radius * Math.sin(angle),
      }
    })
  })

  return positions
}

// ─── Cytoscape stylesheet ────────────────────────────────────────────────────

const cyStylesheet: CyStylesheet[] = [
  {
    selector: 'node',
    style: {
      label: 'data(label)',
      'text-valign': 'bottom',
      'text-halign': 'center',
      'font-size': '10px',
      'text-wrap': 'ellipsis',
      'text-max-width': '100px',
      width: 30,
      height: 30,
      'background-color': '#4A90D9',
      'border-width': 1,
      'border-color': '#333',
      color: '#e0e0e0',
      'text-margin-y': 5,
    },
  },
  {
    selector: 'node[role = "ROOT"]',
    style: {
      'background-color': '#FF6600',
      shape: 'star',
      width: 50,
      height: 50,
      'font-size': '12px',
      'font-weight': 'bold',
      'border-width': 2,
      'border-color': '#CC5200',
    },
  },
  {
    selector: 'node[role = "FUND"]',
    style: {
      'background-color': '#4A90D9',
      shape: 'ellipse',
    },
  },
  {
    selector: 'node[role = "PEER_ONLY"]',
    style: {
      'background-color': '#8BC34A',
      shape: 'diamond',
      width: 25,
      height: 25,
    },
  },
  {
    selector: 'edge',
    style: {
      width: 1.5,
      'line-color': '#555',
      'curve-style': 'bezier',
      opacity: 0.6,
    },
  },
  {
    selector: 'edge[mutual]',
    style: {
      width: 2.5,
      'line-color': '#4A90D9',
      opacity: 0.8,
    },
  },
  {
    selector: 'node:selected',
    style: {
      'border-width': 3,
      'border-color': '#FFD700',
      'background-color': '#FFD700',
    },
  },
]

// ─── Layout configs ──────────────────────────────────────────────────────────

type LayoutName = 'radial' | 'cose' | 'breadthfirst' | 'grid'

const layoutConfigs: Record<LayoutName, object> = {
  radial: { name: 'preset', animate: false, padding: 60 },
  cose: {
    name: 'cose',
    animate: false,
    nodeRepulsion: () => 100000,
    idealEdgeLength: () => 200,
    edgeElasticity: () => 50,
    gravity: 0.02,
    numIter: 2500,
    nodeOverlap: 50,
    padding: 80,
  },
  breadthfirst: {
    name: 'breadthfirst',
    animate: false,
    directed: false,
    spacingFactor: 2.0,
    padding: 80,
  },
  grid: {
    name: 'grid',
    animate: false,
    spacingFactor: 2.0,
    padding: 80,
  },
}

// ─── Main component ──────────────────────────────────────────────────────────

export default function NetworkAnalysis() {
  // Fund selection
  const [selectedFund, setSelectedFund] = useState<BrowseFund | null>(null)

  // Browse filters
  const [searchInput, setSearchInput] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [peersFilter, setPeersFilter] = useState<'all' | 'with' | 'without'>('with')
  const [page, setPage] = useState(1)

  // Debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(searchInput)
      setPage(1)
    }, 300)
    return () => clearTimeout(timer)
  }, [searchInput])

  // Graph controls
  const [depth, setDepth] = useState(2)
  const [edgeOrder, setEdgeOrder] = useState<'1st' | '2nd'>('1st')
  const [relationship, setRelationship] = useState<'mutual' | 'focus' | 'full'>('mutual')
  const [layout, setLayout] = useState<LayoutName>('radial')
  const [showMetrics, setShowMetrics] = useState(false)
  const [activeTab, setActiveTab] = useState<'graph' | 'table' | 'peers'>('graph')

  // Client-side filters
  const [selectedBuckets, setSelectedBuckets] = useState<string[]>([])
  const [bucketsInitialized, setBucketsInitialized] = useState(false)
  const [bucketsExpanded, setBucketsExpanded] = useState(false)
  const [bucketSearch, setBucketSearch] = useState('')

  // Metrics range filters
  const [metricFilters, setMetricFilters] = useState<Record<string, [number, number]>>({})

  // Cytoscape ref
  const cyRef = useRef<cytoscape.Core | null>(null)

  // Browse query
  const { data: browseData, isLoading: browseLoading } = useQuery<BrowseResponse>({
    queryKey: ['network-browse', searchQuery, page, peersFilter],
    queryFn: () =>
      api
        .get('/network/browse-funds', {
          params: { q: searchQuery, page, page_size: 50, peers: peersFilter },
        })
        .then((r) => r.data),
    enabled: !selectedFund,
    staleTime: 60_000,
  })

  // Graph query
  const {
    data: graphData,
    isLoading: graphLoading,
    error: graphError,
  } = useQuery<GraphResponse>({
    queryKey: ['network-graph-full', selectedFund?.fund_id, depth, edgeOrder, relationship, showMetrics],
    queryFn: () =>
      api
        .get('/network/graph/full', {
          params: {
            fund_id: selectedFund!.fund_id,
            max_depth: depth,
            edge_order: edgeOrder,
            relationship,
            include_metrics: showMetrics,
          },
        })
        .then((r) => r.data),
    enabled: !!selectedFund,
    staleTime: 120_000,
  })

  // Peer details query
  const { data: peerDetails } = useQuery<{ fund_id: number; peers: PeerBucket[] }>({
    queryKey: ['network-peers', selectedFund?.fund_id],
    queryFn: () =>
      api.get(`/network/peers/${selectedFund!.fund_id}`).then((r) => r.data),
    enabled: !!selectedFund && activeTab === 'peers',
    staleTime: 300_000,
  })

  // Init bucket filter when graph data arrives
  useEffect(() => {
    if (graphData?.all_buckets && !bucketsInitialized) {
      setSelectedBuckets(graphData.all_buckets)
      setBucketsInitialized(true)
    }
  }, [graphData, bucketsInitialized])

  // Reset bucket filter when fund/controls change
  useEffect(() => {
    setBucketsInitialized(false)
    setSelectedBuckets([])
    setMetricFilters({})
  }, [selectedFund, depth, edgeOrder, relationship])

  // ─── Client-side filtering ────────────────────────────────────────────────

  const filteredData = useMemo(() => {
    if (!graphData) return { nodes: [], edges: [] }

    const rootId = String(graphData.stats.root_fund_id)
    let nodes = graphData.nodes

    // Bucket filter
    if (selectedBuckets.length > 0 && selectedBuckets.length < (graphData.all_buckets?.length || 0)) {
      const bucketSet = new Set(selectedBuckets)
      nodes = nodes.filter(
        (n) => n.id === rootId || n.buckets.some((b) => bucketSet.has(b)),
      )
    }

    // Metrics range filters
    if (showMetrics && Object.keys(metricFilters).length > 0) {
      nodes = nodes.filter((n) => {
        if (n.id === rootId) return true
        for (const [key, [lo, hi]] of Object.entries(metricFilters)) {
          const val = (n as unknown as Record<string, unknown>)[key] as number | undefined
          if (val != null && (val < lo || val > hi)) return false
        }
        return true
      })
    }

    // Filter edges to only include visible nodes
    const nodeIds = new Set(nodes.map((n) => n.id))
    const edges = graphData.edges.filter(
      (e) => nodeIds.has(e.source) && nodeIds.has(e.target),
    )

    return { nodes, edges }
  }, [graphData, selectedBuckets, metricFilters, showMetrics])

  // ─── Cytoscape elements ───────────────────────────────────────────────────

  const cyElements = useMemo(() => {
    if (!filteredData.nodes.length) return []

    const rootId = graphData ? String(graphData.stats.root_fund_id) : ''
    const radialPos = layout === 'radial'
      ? computeRadialPositions(filteredData.nodes, rootId, 1200, 900)
      : {}

    const nodeElements = filteredData.nodes.map((n) => ({
      data: {
        id: n.id,
        label: n.label,
        role: n.role,
        depth: n.depth,
        strategy: n.strategy,
        mutual: false,
      },
      position: radialPos[n.id] || undefined,
    }))

    const edgeElements = filteredData.edges.map((e, i) => ({
      data: {
        id: `e-${i}`,
        source: e.source,
        target: e.target,
        mutual: e.mutual,
        bucket: e.bucket,
      },
    }))

    return [...nodeElements, ...edgeElements]
  }, [filteredData, layout, graphData])

  // ─── Cytoscape layout application ─────────────────────────────────────────

  const handleCyRef = useCallback(
    (cy: cytoscape.Core) => {
      cyRef.current = cy
      if (layout !== 'radial') {
        cy.layout(layoutConfigs[layout] as cytoscape.LayoutOptions).run()
      }
      cy.fit(undefined, 40)
    },
    [layout],
  )

  // ─── Metric range computation ─────────────────────────────────────────────

  const metricRanges = useMemo(() => {
    if (!graphData || !showMetrics) return {}
    const keys = ['ann_return', 'sharpe_ratio', 'max_drawdown', 'volatility'] as const
    const labels: Record<string, string> = {
      ann_return: 'Ann. Return',
      sharpe_ratio: 'Sharpe',
      max_drawdown: 'Max DD',
      volatility: 'Volatility',
    }
    const ranges: Record<string, { min: number; max: number; label: string }> = {}
    for (const key of keys) {
      const vals = graphData.nodes
        .map((n) => (n as unknown as Record<string, unknown>)[key] as number | undefined)
        .filter((v): v is number => v != null)
      if (vals.length > 1) {
        ranges[key] = {
          min: Math.min(...vals),
          max: Math.max(...vals),
          label: labels[key],
        }
      }
    }
    return ranges
  }, [graphData, showMetrics])

  const roleLabels: Record<string, { text: string; cls: string }> = {
    ROOT: { text: 'Root', cls: 'bg-orange-100 text-orange-700' },
    FUND: { text: 'Fund', cls: 'bg-blue-100 text-blue-700' },
    PEER_ONLY: { text: 'Peer', cls: 'bg-green-100 text-green-700' },
  }

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold">Network Analysis</h1>
        <p className="text-muted-foreground">
          Explore Albourne peer fund relationships via BFS graph traversal
        </p>
      </div>

      {/* ── Selected fund banner ─────────────────────────────────────────── */}
      {selectedFund && (
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <CheckCircle className="h-5 w-5 text-green-600 shrink-0" />
              <div>
                <span className="font-semibold">{selectedFund.fund_name}</span>
                {selectedFund.manager_name && (
                  <span className="text-muted-foreground ml-2">
                    ({selectedFund.manager_name})
                  </span>
                )}
                <div className="text-xs text-muted-foreground">
                  Albourne ID: {selectedFund.fund_id}
                </div>
              </div>
            </div>
            <button
              onClick={() => {
                setSelectedFund(null)
                setBucketsInitialized(false)
              }}
              className="h-8 px-3 border border-border rounded text-sm hover:bg-muted flex items-center gap-1"
            >
              <X className="h-3.5 w-3.5" />
              Change Fund
            </button>
          </div>

          {/* ── Controls row ──────────────────────────────────────────── */}
          <div className="mt-3 flex flex-wrap gap-4 items-end">
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Depth</label>
              <select
                value={depth}
                onChange={(e) => setDepth(Number(e.target.value))}
                className="px-2 py-1.5 bg-background border border-border rounded text-sm"
              >
                <option value={1}>1 hop</option>
                <option value={2}>2 hops</option>
                <option value={3}>3 hops</option>
              </select>
            </div>

            <div>
              <label className="text-xs text-muted-foreground block mb-1">Edge Order</label>
              <select
                value={edgeOrder}
                onChange={(e) => setEdgeOrder(e.target.value as '1st' | '2nd')}
                className="px-2 py-1.5 bg-background border border-border rounded text-sm"
              >
                <option value="1st">1st order (direct peers)</option>
                <option value="2nd">2nd order (cross-peer links)</option>
              </select>
            </div>

            <div>
              <label className="text-xs text-muted-foreground block mb-1">Relationship</label>
              <select
                value={relationship}
                onChange={(e) =>
                  setRelationship(e.target.value as 'mutual' | 'focus' | 'full')
                }
                className="px-2 py-1.5 bg-background border border-border rounded text-sm"
              >
                <option value="mutual">Mutual (2-sided)</option>
                <option value="focus">Focus Fund Peers</option>
                <option value="full">Full Network</option>
              </select>
            </div>

            <div>
              <label className="text-xs text-muted-foreground block mb-1">Layout</label>
              <select
                value={layout}
                onChange={(e) => setLayout(e.target.value as LayoutName)}
                className="px-2 py-1.5 bg-background border border-border rounded text-sm"
              >
                <option value="radial">Radial (root centered)</option>
                <option value="cose">Force-directed (cose)</option>
                <option value="breadthfirst">Tree (breadthfirst)</option>
                <option value="grid">Grid</option>
              </select>
            </div>

            <label className="flex items-center gap-1.5 text-sm pb-1">
              <input
                type="checkbox"
                checked={showMetrics}
                onChange={(e) => setShowMetrics(e.target.checked)}
                className="rounded"
              />
              Load Metrics
            </label>
          </div>
        </div>
      )}

      {/* ── Fund browser ─────────────────────────────────────────────────── */}
      {!selectedFund && (
        <div className="bg-card border border-border rounded-lg">
          <div className="p-4 border-b border-border">
            <div className="flex items-center gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                <input
                  type="text"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Search by fund name or manager..."
                  className="w-full h-10 pl-9 pr-3 border border-input rounded-md bg-background text-sm"
                />
              </div>
              <select
                value={peersFilter}
                onChange={(e) => {
                  setPeersFilter(e.target.value as 'all' | 'with' | 'without')
                  setPage(1)
                }}
                title="Filter by cached peer availability"
                className="h-10 px-2 border border-input rounded-md bg-background text-sm"
              >
                <option value="with">With peers</option>
                <option value="without">Without peers</option>
                <option value="all">All funds</option>
              </select>
              {browseData && (
                <span className="text-xs text-muted-foreground whitespace-nowrap">
                  {browseData.total.toLocaleString()} funds
                  {browseData.cached_peer_count > 0 && (
                    <> | {browseData.cached_peer_count} with peer data</>
                  )}
                </span>
              )}
            </div>
          </div>

          {browseLoading ? (
            <div className="flex justify-center p-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : browseData && browseData.funds.length > 0 ? (
            <>
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th className="w-8"></th>
                      <th>Fund Name</th>
                      <th>Manager</th>
                      <th className="w-20 text-right">ID</th>
                      <th className="w-24">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {browseData.funds.map((fund) => (
                      <tr key={fund.fund_id} className="hover:bg-muted/50">
                        <td>
                          {fund.has_peer_data && (
                            <span title="Has cached peer data">
                              <Database className="h-3.5 w-3.5 text-green-500" />
                            </span>
                          )}
                        </td>
                        <td className="font-medium">{fund.fund_name}</td>
                        <td className="text-sm">{fund.manager_name || '-'}</td>
                        <td className="text-right text-sm text-muted-foreground font-mono">
                          {fund.fund_id}
                        </td>
                        <td>
                          <button
                            onClick={() => setSelectedFund(fund)}
                            disabled={!fund.has_peer_data}
                            title={
                              fund.has_peer_data
                                ? undefined
                                : 'No cached peers — this fund would render as a single isolated node'
                            }
                            className="h-7 px-3 bg-primary text-primary-foreground rounded text-xs font-medium hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-primary"
                          >
                            {fund.has_peer_data ? 'Select' : 'No peers'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {browseData.total_pages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t border-border">
                  <span className="text-sm text-muted-foreground">
                    Page {browseData.page} of {browseData.total_pages}
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="h-8 px-3 border border-border rounded text-sm hover:bg-muted disabled:opacity-50 flex items-center gap-1"
                    >
                      <ChevronLeft className="h-4 w-4" /> Previous
                    </button>
                    <button
                      onClick={() =>
                        setPage((p) => Math.min(browseData.total_pages, p + 1))
                      }
                      disabled={page === browseData.total_pages}
                      className="h-8 px-3 border border-border rounded text-sm hover:bg-muted disabled:opacity-50 flex items-center gap-1"
                    >
                      Next <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="p-8 text-center text-muted-foreground text-sm">
              {peersFilter === 'with' && browseData && browseData.match_without_peers > 0 ? (
                <>
                  {browseData.match_without_peers.toLocaleString()} matching fund
                  {browseData.match_without_peers > 1 ? 's have' : ' has'} no cached peers
                  (nothing to graph).{' '}
                  <button
                    onClick={() => {
                      setPeersFilter('all')
                      setPage(1)
                    }}
                    className="text-primary underline hover:no-underline font-medium"
                  >
                    Show all funds
                  </button>
                </>
              ) : peersFilter === 'without' && browseData && browseData.match_with_peers > 0 ? (
                <>
                  {browseData.match_with_peers.toLocaleString()} matching fund
                  {browseData.match_with_peers > 1 ? 's have' : ' has'} cached peers.{' '}
                  <button
                    onClick={() => {
                      setPeersFilter('with')
                      setPage(1)
                    }}
                    className="text-primary underline hover:no-underline font-medium"
                  >
                    Show funds with peers
                  </button>
                </>
              ) : (
                'No Albourne funds found matching your filters.'
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Loading / Error ──────────────────────────────────────────────── */}
      {selectedFund && graphLoading && (
        <div className="flex items-center justify-center gap-3 p-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          <span className="text-muted-foreground">
            Building network graph{showMetrics ? ' with metrics (this may take a moment)' : ''}...
          </span>
        </div>
      )}
      {selectedFund && graphError && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-700">
          No peer data cached for this fund. Peer data needs to be loaded from
          the Albourne API first.
        </div>
      )}

      {/* ── Graph results ────────────────────────────────────────────────── */}
      {selectedFund && graphData && filteredData.nodes.length > 0 && (
        <>
          {/* Bucket filter — collapsible */}
          {graphData.all_buckets.length > 1 && (() => {
            const total = graphData.all_buckets.length
            // Treat empty selectedBuckets as "all selected" (filter logic already does this)
            const selected = selectedBuckets.length === 0 ? total : selectedBuckets.length
            const allSelected = selected === total
            const COLLAPSED_MAX = 8
            const shortName = (b: string) =>
              b.replace(/ - Albourne Strategy Group/g, '').replace(/Albourne /g, '')

            // When collapsed, show first N buckets that are deselected (to hint at what's filtered)
            const visibleBuckets = bucketsExpanded
              ? (bucketSearch
                  ? graphData.all_buckets.filter((b) =>
                      shortName(b).toLowerCase().includes(bucketSearch.toLowerCase()),
                    )
                  : graphData.all_buckets)
              : graphData.all_buckets.slice(0, COLLAPSED_MAX)

            return (
              <div className="bg-card border border-border rounded-lg">
                {/* Header — always visible */}
                <button
                  onClick={() => setBucketsExpanded((v) => !v)}
                  className="w-full flex items-center gap-2 p-3 hover:bg-muted/30 transition-colors"
                >
                  <SlidersHorizontal className="h-4 w-4 text-muted-foreground shrink-0" />
                  <span className="text-xs font-medium">
                    Strategy Buckets
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {selected === total
                      ? `All ${total} selected`
                      : `${selected} of ${total} selected`}
                  </span>
                  <div className="ml-auto flex items-center gap-2">
                    {!allSelected && (
                      <span
                        onClick={(e) => {
                          e.stopPropagation()
                          setSelectedBuckets(graphData.all_buckets)
                        }}
                        className="text-xs text-primary hover:underline"
                      >
                        Select All
                      </span>
                    )}
                    {allSelected && (
                      <span
                        onClick={(e) => {
                          e.stopPropagation()
                          setSelectedBuckets([])
                        }}
                        className="text-xs text-primary hover:underline"
                      >
                        Deselect All
                      </span>
                    )}
                    {bucketsExpanded ? (
                      <ChevronUp className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    )}
                  </div>
                </button>

                {/* Collapsed preview — show compact chips */}
                {!bucketsExpanded && (
                  <div className="px-3 pb-3 flex items-center gap-1.5 flex-wrap">
                    {visibleBuckets.map((b) => {
                      const active = allSelected || selectedBuckets.includes(b)
                      return (
                        <button
                          key={b}
                          onClick={() => {
                            if (allSelected && selectedBuckets.length === 0) {
                              setSelectedBuckets(graphData.all_buckets.filter((x) => x !== b))
                            } else {
                              setSelectedBuckets((prev) =>
                                active ? prev.filter((x) => x !== b) : [...prev, b],
                              )
                            }
                          }}
                          className={`px-2 py-0.5 rounded text-xs border transition-colors ${
                            active
                              ? 'bg-primary text-primary-foreground border-primary'
                              : 'bg-muted text-muted-foreground border-border hover:border-primary'
                          }`}
                          title={b}
                        >
                          {shortName(b)}
                        </button>
                      )
                    })}
                    {total > COLLAPSED_MAX && (
                      <button
                        onClick={() => setBucketsExpanded(true)}
                        className="text-xs text-primary hover:underline"
                      >
                        +{total - COLLAPSED_MAX} more...
                      </button>
                    )}
                  </div>
                )}

                {/* Expanded — search + full list */}
                {bucketsExpanded && (
                  <div className="px-3 pb-3 space-y-2">
                    {total > 15 && (
                      <div className="relative">
                        <Search className="absolute left-2.5 top-2 h-3.5 w-3.5 text-muted-foreground" />
                        <input
                          type="text"
                          value={bucketSearch}
                          onChange={(e) => setBucketSearch(e.target.value)}
                          placeholder="Filter buckets..."
                          className="w-full h-8 pl-8 pr-3 border border-input rounded bg-background text-xs"
                        />
                      </div>
                    )}
                    <div className="flex flex-wrap gap-1.5 max-h-48 overflow-y-auto">
                      {visibleBuckets.map((b) => {
                        const active = allSelected || selectedBuckets.includes(b)
                        return (
                          <button
                            key={b}
                            onClick={() => {
                              if (allSelected && selectedBuckets.length === 0) {
                                // First deselection from "all via empty" — materialize all then remove this one
                                setSelectedBuckets(graphData.all_buckets.filter((x) => x !== b))
                              } else {
                                setSelectedBuckets((prev) =>
                                  active ? prev.filter((x) => x !== b) : [...prev, b],
                                )
                              }
                            }}
                            className={`px-2 py-0.5 rounded text-xs border transition-colors ${
                              active
                                ? 'bg-primary text-primary-foreground border-primary'
                                : 'bg-muted text-muted-foreground border-border hover:border-primary'
                            }`}
                            title={b}
                          >
                            {shortName(b)}
                          </button>
                        )
                      })}
                      {visibleBuckets.length === 0 && (
                        <span className="text-xs text-muted-foreground py-2">
                          No buckets matching &quot;{bucketSearch}&quot;
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )
          })()}

          {/* Metrics range filters */}
          {showMetrics && Object.keys(metricRanges).length > 0 && (
            <div className="bg-card border border-border rounded-lg p-3">
              <div className="flex items-center gap-2 mb-2">
                <SlidersHorizontal className="h-4 w-4 text-muted-foreground" />
                <span className="text-xs text-muted-foreground uppercase">Metric Filters</span>
                {Object.keys(metricFilters).length > 0 && (
                  <button
                    onClick={() => setMetricFilters({})}
                    className="text-xs text-primary hover:underline ml-auto"
                  >
                    Reset
                  </button>
                )}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {Object.entries(metricRanges).map(([key, { min, max, label }]) => {
                  const cur = metricFilters[key] || [min, max]
                  const step = key === 'sharpe_ratio' ? 0.05 : 0.005
                  const fmt = key === 'sharpe_ratio' ? (v: number) => v.toFixed(2) : (v: number) => (v * 100).toFixed(1) + '%'
                  return (
                    <div key={key}>
                      <div className="text-xs text-muted-foreground mb-1">
                        {label}: {fmt(cur[0])} — {fmt(cur[1])}
                      </div>
                      <div className="flex gap-1 items-center">
                        <input
                          type="range"
                          min={min}
                          max={max}
                          step={step}
                          value={cur[0]}
                          onChange={(e) =>
                            setMetricFilters((prev) => ({
                              ...prev,
                              [key]: [Number(e.target.value), cur[1]],
                            }))
                          }
                          className="w-full h-1.5 accent-primary"
                        />
                        <input
                          type="range"
                          min={min}
                          max={max}
                          step={step}
                          value={cur[1]}
                          onChange={(e) =>
                            setMetricFilters((prev) => ({
                              ...prev,
                              [key]: [cur[0], Number(e.target.value)],
                            }))
                          }
                          className="w-full h-1.5 accent-primary"
                        />
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {/* Tab bar */}
          <div className="flex border-b border-border">
            {(
              [
                { key: 'graph', icon: Network, label: 'Interactive Graph' },
                { key: 'table', icon: Table2, label: 'Summary Table' },
                { key: 'peers', icon: FileText, label: 'Peer Details' },
              ] as const
            ).map(({ key, icon: Icon, label }) => (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-1.5 ${
                  activeTab === key
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon className="h-4 w-4" />
                {label}
              </button>
            ))}
            <div className="ml-auto flex items-center px-4 gap-3 text-sm text-muted-foreground">
              <span>{filteredData.nodes.length} nodes</span>
              <span>{filteredData.edges.length} edges</span>
            </div>
          </div>

          {/* ── Graph View ─────────────────────────────────────────────── */}
          {activeTab === 'graph' && (
            <div className="bg-card border border-border rounded-lg overflow-hidden">
              <div style={{ height: 700 }}>
                <CytoscapeComponent
                  key={`${layout}-${filteredData.nodes.length}-${filteredData.edges.length}`}
                  elements={cyElements}
                  stylesheet={cyStylesheet}
                  layout={layoutConfigs[layout] as cytoscape.LayoutOptions}
                  style={{ width: '100%', height: '100%' }}
                  cy={(cy) => handleCyRef(cy)}
                  minZoom={0.2}
                  maxZoom={3}
                  wheelSensitivity={0.3}
                />
              </div>

              {/* Legend + Stats */}
              <div className="border-t border-border p-3">
                <div className="flex flex-wrap gap-4 items-center">
                  <div className="flex items-center gap-4 text-xs">
                    <span className="flex items-center gap-1">
                      <span className="inline-block w-3 h-3 bg-orange-500 rounded-sm" style={{ clipPath: 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)' }} />
                      Root fund
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="inline-block w-3 h-3 bg-blue-500 rounded-full" />
                      Fund (has peer data)
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="inline-block w-3 h-3 bg-green-500 rounded-sm rotate-45" />
                      Peer only
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="inline-block w-8 h-0.5 bg-blue-500" />
                      Mutual
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="inline-block w-8 h-0.5 bg-gray-400" />
                      Directional
                    </span>
                  </div>
                  <div className="ml-auto flex items-center gap-3 text-xs text-muted-foreground">
                    <span>
                      Mutual: {filteredData.edges.filter((e) => e.mutual).length}
                    </span>
                    <span>
                      1st order: {filteredData.edges.filter((e) => e.order === 1).length}
                    </span>
                    <span>
                      2nd order: {filteredData.edges.filter((e) => e.order === 2).length}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── Table View ─────────────────────────────────────────────── */}
          {activeTab === 'table' && (
            <div className="bg-card border border-border rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Fund</th>
                      <th className="text-right">Depth</th>
                      <th>Role</th>
                      <th>Strategy</th>
                      <th>Buckets</th>
                      {showMetrics && (
                        <>
                          <th className="text-right">Ann Return</th>
                          <th className="text-right">Sharpe</th>
                          <th className="text-right">Max DD</th>
                          <th className="text-right">Vol</th>
                          <th className="text-right">Sortino</th>
                          <th className="text-right">Calmar</th>
                          <th className="text-right">Skew</th>
                          <th className="text-right">Kurt</th>
                          <th className="text-right">Best Mo</th>
                          <th className="text-right">Worst Mo</th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredData.nodes
                      .sort((a, b) => a.depth - b.depth || a.label.localeCompare(b.label))
                      .map((node) => (
                        <tr
                          key={node.id}
                          className={node.role === 'ROOT' ? 'bg-primary/5 font-semibold' : ''}
                        >
                          <td className="font-medium">
                            <div className="flex items-center gap-2">
                              {node.has_peer_data && (
                                <Database className="h-3 w-3 text-green-500 shrink-0" />
                              )}
                              {node.label}
                            </div>
                          </td>
                          <td className="text-right font-mono">{node.depth}</td>
                          <td>
                            <span
                              className={`inline-block px-1.5 py-0.5 rounded text-xs ${
                                roleLabels[node.role]?.cls || 'bg-muted'
                              }`}
                            >
                              {roleLabels[node.role]?.text || node.role}
                            </span>
                          </td>
                          <td className="text-sm">{node.strategy || '-'}</td>
                          <td className="text-xs">
                            {node.buckets?.length
                              ? node.buckets
                                  .map((b) =>
                                    b
                                      .replace(/ - Albourne Strategy Group/g, '')
                                      .replace(/Albourne /g, ''),
                                  )
                                  .join(', ')
                              : '-'}
                          </td>
                          {showMetrics && (
                            <>
                              <td className="text-right font-mono">
                                {formatPercent(node.ann_return)}
                              </td>
                              <td className="text-right font-mono">
                                {formatNumber(node.sharpe_ratio)}
                              </td>
                              <td className="text-right font-mono text-red-600">
                                {formatPercent(node.max_drawdown)}
                              </td>
                              <td className="text-right font-mono">
                                {formatPercent(node.volatility)}
                              </td>
                              <td className="text-right font-mono">
                                {formatNumber(node.sortino_ratio)}
                              </td>
                              <td className="text-right font-mono">
                                {formatNumber(node.calmar_ratio)}
                              </td>
                              <td className="text-right font-mono">
                                {formatNumber(node.skewness)}
                              </td>
                              <td className="text-right font-mono">
                                {formatNumber(node.kurtosis)}
                              </td>
                              <td className="text-right font-mono text-green-600">
                                {formatPercent(node.best_month)}
                              </td>
                              <td className="text-right font-mono text-red-600">
                                {formatPercent(node.worst_month)}
                              </td>
                            </>
                          )}
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ── Peer Details View ──────────────────────────────────────── */}
          {activeTab === 'peers' && (
            <div className="space-y-4">
              {!peerDetails ? (
                <div className="flex items-center justify-center gap-2 p-8 text-muted-foreground">
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Loading peer details...
                </div>
              ) : (
                peerDetails.peers.map((bucket, i) => (
                  <div
                    key={i}
                    className="bg-card border border-border rounded-lg overflow-hidden"
                  >
                    <div className="p-3 border-b border-border bg-muted/30">
                      <div className="font-semibold text-sm">
                        Bucket {i + 1}: {bucket.fundBucketName}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Type: {bucket.type || '-'} | Strategy:{' '}
                        {bucket.strategy || '-'} | Default:{' '}
                        {bucket.isDefaultFundBucket ? 'Yes' : 'No'}
                      </div>
                    </div>

                    {bucket.peerFunds && bucket.peerFunds.length > 0 && (
                      <div className="overflow-x-auto">
                        <table className="data-table">
                          <thead>
                            <tr>
                              <th>Fund ID</th>
                              <th>Fund Name</th>
                              <th>Manager</th>
                            </tr>
                          </thead>
                          <tbody>
                            {bucket.peerFunds.map((pf) => (
                              <tr key={pf.fundId}>
                                <td className="font-mono text-sm">
                                  {pf.fundId}
                                </td>
                                <td className="font-medium">{pf.fundName}</td>
                                <td className="text-sm">
                                  {pf.managerName || '-'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {bucket.peerIndices && bucket.peerIndices.length > 0 && (
                      <div className="p-3 border-t border-border">
                        <div className="text-xs text-muted-foreground mb-1">
                          Peer Indices:
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {bucket.peerIndices.map((pi) => (
                            <span
                              key={pi.indexId}
                              className="inline-block px-2 py-0.5 rounded bg-muted text-xs"
                            >
                              {pi.indexName} ({pi.indexId})
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}

          {/* ── Relationship stats bar ────────────────────────────────── */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <div className="bg-card border border-border rounded px-3 py-2">
              <div className="text-muted-foreground text-xs">Total Nodes</div>
              <div className="font-semibold">{filteredData.nodes.length}</div>
            </div>
            <div className="bg-card border border-border rounded px-3 py-2">
              <div className="text-muted-foreground text-xs">Total Edges</div>
              <div className="font-semibold">{filteredData.edges.length}</div>
            </div>
            <div className="bg-card border border-border rounded px-3 py-2">
              <div className="text-muted-foreground text-xs">
                Expandable Funds
              </div>
              <div className="font-semibold">
                {filteredData.nodes.filter((n) => n.role === 'FUND').length}
              </div>
            </div>
            <div className="bg-card border border-border rounded px-3 py-2">
              <div className="text-muted-foreground text-xs">
                Mutual Relationships
              </div>
              <div className="font-semibold">
                {filteredData.edges.filter((e) => e.mutual).length}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
